import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, FlaskConical, MapPin, FileText, Upload, CheckCircle2, RefreshCw, X, Home } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { uploadFile } from '@/lib/uploads';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function ProviderLabs() {
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [home, setHome] = useState(true);
  const [quoteForm, setQuoteForm] = useState(null);
  const load = async () => {
    try {
      const p = await api.get('/providers/me');
      if (p.data.provider?.category !== 'lab_test') return;
      setHome(p.data.provider?.lab_home_collection_enabled !== false);
      const [r, o] = await Promise.all([api.get('/providers/me/lab/requests'), api.get('/providers/me/lab/orders')]);
      setRequests(r.data.requests || []);
      setOrders(o.data.orders || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Lab operations');
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const toggleHome = async () => {
    try {
      const { data } = await api.patch('/providers/me/lab/home-collection', { enabled: !home });
      setHome(Boolean(data.enabled));
      toast.success(data.enabled ? 'Home collection enabled' : 'Home collection disabled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
  };

  const openQuote = (request) => {
    setQuoteForm({
      orderId: request.order_id,
      amount: '',
      reportTat: '24',
      mode: request.order?.collection_mode === 'lab_visit' ? 'lab_visit' : (home ? 'home_collection' : 'lab_visit'),
    });
  };

  const sendQuote = async () => {
    if (!quoteForm) return;
    const amount = Number(quoteForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return toast.error('Enter a valid quotation');
    try {
      await api.post('/providers/me/lab/quote', {
        order_id: quoteForm.orderId,
        amount,
        report_tat_hours: Number(quoteForm.reportTat) || 24,
        collection_mode: quoteForm.mode,
      });
      toast.success('Quotation sent. RESQLY calculates the ETA and compares all labs.');
      setQuoteForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send quotation');
    }
  };

  const step = async (id, name) => {
    try { await api.post('/providers/me/lab/orders/' + id + '/step/' + name); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update order'); }
  };

  const report = async (id) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        const url = await uploadFile('lab-reports', file);
        await api.post('/providers/me/lab/orders/' + id + '/report', { report_url: url, title: 'Lab report' });
        toast.success('Report uploaded');
        load();
      } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Could not upload report'); }
    };
    input.click();
  };

  return (
    <MobileShell title="Lab Operations">
      <div className="px-5 py-5 pb-10">
        <button onClick={() => window.history.back()} className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</button>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-[30px] bg-slate-950 text-white p-6">
          <div className="text-[10px] tracking-[.2em] uppercase text-violet-300 font-bold">Lab partner</div>
          <div className="text-2xl font-black mt-1">Respond to requests. Win on price.</div>
          <p className="text-sm text-white/60 mt-2">No busy switch. Your lab can quote multiple customers at once. RESQLY calculates customer ETA from your live location.</p>
          <button onClick={toggleHome} className="mt-4 rounded-2xl bg-white/10 border border-white/10 px-4 py-3 text-sm font-bold inline-flex items-center gap-2">
            <Home className="w-4 h-4" /> {home ? 'Home collection: ON' : 'Home collection: OFF'}
          </button>
        </motion.div>

        <div className="mt-5 flex items-center justify-between">
          <div className="text-lg font-black">Incoming requests</div>
          <button onClick={load} className="p-2 rounded-full bg-slate-100"><RefreshCw className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3 mt-3">
          {requests.map((r) => (
            <div key={r.id} className="rounded-3xl bg-white border p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-violet-50 text-violet-700 flex items-center justify-center"><FlaskConical className="w-5 h-5" /></div>
                <div className="flex-1">
                  <div className="font-black">{r.order?.request_type === 'prescription' ? 'Prescription request' : 'Customer lab request'}</div>
                  <div className="text-sm text-slate-600 mt-1">{r.order?.request_type === 'prescription' ? 'Open the prescription and review what the customer needs.' : (r.order?.request_text || 'Customer did not add a description.')}</div>
                  <div className="flex flex-wrap gap-2 mt-3 text-[11px]">
                    <span className="px-2 py-1 rounded-full bg-slate-100 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> Nearby customer</span>
                    <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-bold">ETA calculated by RESQLY</span>
                  </div>
                  {r.prescription?.image_url && (
                    <a href={r.prescription.image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-violet-700 underline"><FileText className="w-4 h-4" /> Open prescription</a>
                  )}
                </div>
              </div>
              <Button onClick={() => openQuote(r)} className="w-full mt-4 bg-violet-700">Send quotation</Button>
            </div>
          ))}
          {!requests.length && <div className="rounded-3xl bg-slate-50 border p-8 text-center text-sm text-slate-500">No open requests right now.</div>}
        </div>

        <div className="text-lg font-black mt-8">My lab bookings</div>
        <div className="space-y-3 mt-3">
          {orders.map((o) => (
            <div key={o.id} className="rounded-3xl bg-white border p-4">
              <div className="flex justify-between gap-3"><div className="font-black">#{o.id.slice(0, 8)}</div><span className="text-xs font-bold text-violet-700">{o.status}</span></div>
              <div className="text-xs text-slate-500 mt-2">{o.request_type === 'prescription' ? 'Prescription request' : (o.request_text || 'Lab request')}</div>
              {o.pricing_snapshot?.total != null && <div className="text-sm font-bold text-emerald-700 mt-2">₹{Number(o.pricing_snapshot.total).toFixed(0)} · customer ETA {o.pricing_snapshot.eta_minutes || '—'} min</div>}
              {o.status === 'confirmed' && <Button className="w-full mt-3" onClick={() => step(o.id, o.collection_mode === 'home_collection' ? 'en_route' : 'collect')}>{o.collection_mode === 'home_collection' ? 'Start collection trip' : 'Start sample collection'}</Button>}
              {o.status === 'collector_en_route' && <Button className="w-full mt-3" onClick={() => step(o.id, 'arrived')}>Arrived at customer</Button>}
              {o.status === 'collector_arrived' && <Button className="w-full mt-3" onClick={() => step(o.id, 'collect')}>Sample collected</Button>}
              {o.status === 'sample_collected' && <Button className="w-full mt-3" onClick={() => step(o.id, 'processing')}>Start processing</Button>}
              {o.status === 'processing' && <Button className="w-full mt-3 bg-emerald-700" onClick={() => report(o.id)}><Upload className="w-4 h-4 mr-2" /> Upload report</Button>}
              {o.status === 'report_ready' && <div className="text-sm text-emerald-700 font-bold mt-3 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Report delivered to customer</div>}
            </div>
          ))}
          {!orders.length && <div className="rounded-3xl bg-slate-50 border p-8 text-center text-sm text-slate-500">Accepted lab bookings will appear here.</div>}
        </div>

        {quoteForm && (
          <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-end sm:items-center justify-center p-4" onClick={() => setQuoteForm(null)}>
            <div className="w-full max-w-lg rounded-3xl bg-white p-5 shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between">
                <div><div className="text-[10px] uppercase tracking-widest text-violet-600 font-bold">Lab quotation</div><div className="text-xl font-black mt-1">Set your price</div><div className="text-xs text-slate-500 mt-1">Do not enter ETA. RESQLY calculates road ETA from your lab to the customer.</div></div>
                <button onClick={() => setQuoteForm(null)}><X /></button>
              </div>
              <Input type="number" min="1" value={quoteForm.amount} onChange={(e) => setQuoteForm({ ...quoteForm, amount: e.target.value })} placeholder="Quotation ₹" />
              <Input type="number" min="1" value={quoteForm.reportTat} onChange={(e) => setQuoteForm({ ...quoteForm, reportTat: e.target.value })} placeholder="Report turnaround hours" />
              <div className="text-xs text-slate-500">Collection mode: {quoteForm.mode === 'home_collection' ? 'Home collection' : 'Lab visit'}</div>
              <Button className="w-full bg-violet-700" onClick={sendQuote}>Send quotation</Button>
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}
